import { useCallback, useEffect, useRef, useState } from 'react';

import {
    TestMessageResultItem,
    TestMessageResultStatus,
} from '../../models/formula.model';

import useTestMessageResultsQuery from './useTestMessageResultsQuery';

export const DELIVERY_POLL_INTERVAL_MS = 3000;
export const DELIVERY_POLL_TIMEOUT_MS = 60000;

export type TestMessageDeliveryPhase =
    | 'checking'
    | 'delivered'
    | 'failed'
    | 'timed_out';

export const computeDeliveryPhase = (
    rollupStatus: TestMessageResultStatus | undefined,
    elapsedMs: number,
): TestMessageDeliveryPhase => {
    if (rollupStatus === TestMessageResultStatus.Succeeded) return 'delivered';
    if (rollupStatus === TestMessageResultStatus.Failed) return 'failed';
    return elapsedMs >= DELIVERY_POLL_TIMEOUT_MS ? 'timed_out' : 'checking';
};

export interface UseTestMessageDeliveryStatusResult {
    phase: TestMessageDeliveryPhase;
    results: TestMessageResultItem[];
    checkAgain: () => void;
}

const useTestMessageDeliveryStatus = (
    requestId: string | undefined,
): UseTestMessageDeliveryStatusResult => {
    const startedAtRef = useRef<number | null>(null);
    const [phase, setPhase] = useState<TestMessageDeliveryPhase>('checking');

    useEffect(() => {
        startedAtRef.current = requestId ? Date.now() : null;
        setPhase('checking');

        if (!requestId) return undefined;

        // Arm a deadline timer independent of the network: refetchInterval
        // only reschedules after a poll settles, so if every poll rejects
        // (or the very first response never arrives), `data`/`dataUpdatedAt`
        // never change and the data-driven effect below never re-runs to
        // notice the deadline has passed. This timer guarantees we still
        // leave `checking` after DELIVERY_POLL_TIMEOUT_MS regardless of
        // whether any poll ever resolved.
        const deadlineTimer = setTimeout(() => {
            setPhase((current) =>
                current === 'checking' ? 'timed_out' : current,
            );
        }, DELIVERY_POLL_TIMEOUT_MS);

        return () => clearTimeout(deadlineTimer);
    }, [requestId]);

    const isPolling = phase === 'checking';

    const { data, dataUpdatedAt, refetch } = useTestMessageResultsQuery(
        requestId,
        {
            enabled: !!requestId,
            refetchInterval: isPolling ? DELIVERY_POLL_INTERVAL_MS : false,
        },
    );

    useEffect(() => {
        if (!requestId || startedAtRef.current === null || !isPolling) return;
        const elapsedMs = Date.now() - startedAtRef.current;
        const nextPhase = computeDeliveryPhase(data?.data?.status, elapsedMs);
        if (nextPhase !== 'checking') setPhase(nextPhase);
    }, [data, dataUpdatedAt, isPolling, requestId]);

    const checkAgain = useCallback(() => {
        if (phase !== 'timed_out') return;
        refetch()
            .then((res) => {
                const latestStatus = res.data?.data?.status;
                if (latestStatus === TestMessageResultStatus.Succeeded) {
                    setPhase('delivered');
                } else if (latestStatus === TestMessageResultStatus.Failed) {
                    setPhase('failed');
                }
            })
            .catch(() => {});
    }, [phase, refetch]);

    return {
        phase,
        results: data?.data?.results ?? [],
        checkAgain,
    };
};

export default useTestMessageDeliveryStatus;
